/**
 * Shared password validation utilities for Register, CompanySignup,
 * ResetPassword and any other flows that collect or validate passwords.
 */

/** Result of validating a password against policy requirements */
export interface PasswordValidation {
  minLength: boolean;
  hasLowercase: boolean;
  hasUppercase: boolean;
  hasNumber: boolean;
  hasSpecialChar: boolean;
}

/** Default minimum length required for passwords */
const PASSWORD_MIN_LENGTH = 8;

/** Regex for at least one lowercase letter (a-z) */
const LOWERCASE_REGEX = /[a-z]/;

/** Regex for at least one uppercase letter (A-Z) */
const UPPERCASE_REGEX = /[A-Z]/;

/** Regex for at least one digit (0-9) */
const NUMBER_REGEX = /\d/;

/** Regex for at least one special character */
const SPECIAL_CHAR_REGEX = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/;

/**
 * Validates a password string against the shared policy requirements.
 * Use this to drive real-time UI (e.g. PasswordPolicyIndicator) and submit checks.
 */
export function validatePasswordRequirements(
  password: string
): PasswordValidation {
  return {
    minLength: password.length >= PASSWORD_MIN_LENGTH,
    hasLowercase: LOWERCASE_REGEX.test(password),
    hasUppercase: UPPERCASE_REGEX.test(password),
    hasNumber: NUMBER_REGEX.test(password),
    hasSpecialChar: SPECIAL_CHAR_REGEX.test(password),
  };
}

/**
 * Returns true only if every requirement in the validation object is satisfied.
 */
export function isPasswordValid(validation: PasswordValidation): boolean {
  return Object.values(validation).every(Boolean);
}

/**
 * Minimum length constant for use in UI copy (e.g. "At least 8 characters").
 */
export const PASSWORD_POLICY_MIN_LENGTH = PASSWORD_MIN_LENGTH;
